import { useEffect, useRef, useState } from 'react'

import { brand, nav } from '@/content/site'

function Wordmark() {
  return (
    <span className="font-sans text-base font-semibold tracking-tight text-ink select-none">
      <span className="text-iris">◆</span>
      <span className="ml-1.5">{brand.wordmark}</span>
    </span>
  )
}

export function NavBar() {
  const navRef = useRef<HTMLElement | null>(null)
  const lastScroll = useRef(0)
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const el = navRef.current
    if (!el) return

    let frame = 0
    const onScroll = () => {
      const y = window.scrollY ?? 0
      el.style.transform = y > lastScroll.current && y > 120 ? 'translateY(-100%)' : 'translateY(0)'
      setScrolled(y > 40)
      lastScroll.current = y
    }

    const schedule = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        onScroll()
      })
    }

    onScroll()
    window.addEventListener('scroll', schedule, { passive: true })
    return () => {
      window.removeEventListener('scroll', schedule)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <nav
      ref={navRef}
      className={`fixed inset-x-0 top-0 z-50 transition-transform duration-500 ease-glass ${
        scrolled ? 'py-2.5' : 'py-4'
      }`}
    >
      {/* The bar itself is glass, so it tints with whatever aurora is behind it. */}
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${
          scrolled ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background: 'rgb(8 7 15 / 0.55)',
          backdropFilter: 'blur(20px) saturate(170%)',
          WebkitBackdropFilter: 'blur(20px) saturate(170%)',
          borderBottom: '1px solid rgb(255 255 255 / 0.07)',
        }}
        aria-hidden="true"
      />

      <div className="shell relative flex items-center justify-between">
        <a href="#top" className="focus-ring flex items-center rounded-xl" aria-label={nav.logoAria}>
          <Wordmark />
        </a>

        <div className="hidden items-center gap-7 lg:flex">
          {nav.links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="font-paragraph text-[0.9rem] text-ink-dim transition-colors duration-300 hover:text-ink"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <a href={nav.cta.href} className="btn btn-glass focus-ring !px-4 !py-2 !text-[0.66rem]">
            {nav.cta.label}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? nav.menuCloseAria : nav.menuOpenAria}
          className="glass focus-ring inline-flex size-10 items-center justify-center !rounded-xl lg:hidden"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            {open ? (
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            ) : (
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            )}
          </svg>
        </button>
      </div>

      {open && (
        <div
          id="mobile-menu"
          className="glass glass-rim glass-hi absolute left-4 right-4 top-[4.5rem] p-3 lg:hidden"
        >
          {nav.links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={() => setOpen(false)}
              className="block rounded-xl px-3 py-3 font-paragraph text-sm text-ink-dim transition-colors hover:bg-white/5 hover:text-ink"
            >
              {link.label}
            </a>
          ))}
          <a
            href={nav.cta.href}
            onClick={() => setOpen(false)}
            className="btn btn-primary mt-2 w-full"
          >
            {nav.cta.label}
          </a>
        </div>
      )}
    </nav>
  )
}
